"use client"

import React, { useState, useEffect } from "react"
import styled from "styled-components"
import PostCard from "./PostCard"
import { PlusCircle, Loader, BarChart3, Eye, Heart, MessageSquare, Users, Calendar, Filter, Search, Grid, List, Share, Settings, TrendingUp } from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"
import { useNavigate } from "react-router-dom"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    padding: 0 16px;
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 0 12px;
    gap: 12px;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`

const Title = styled.h1`
  font-size: 28px;
  margin: 0;
  color: ${props => props.theme.textPrimary};
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
  
  @media (max-width: 480px) {
    font-size: 22px;
  }
`

const CreatePostButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: ${props => props.theme.buttonPrimaryHover};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`

const FeedTabs = styled.div`
  display: flex;
  border-bottom: 2px solid ${props => props.theme.border};
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px 8px 0 0;
  padding: 0 4px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    border-radius: 8px;
    border: 1px solid ${props => props.theme.border};
  }
`

const FeedTab = styled.button`
  padding: 16px 20px;
  background: none;
  border: none;
  border-bottom: 3px solid ${(props) => (props.$active ? props.theme.buttonPrimary : "transparent")};
  color: ${(props) => (props.$active ? props.theme.buttonPrimary : props.theme.textSecondary)};
  font-weight: ${(props) => (props.$active ? "700" : "500")};
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    color: ${(props) => (props.$active ? props.theme.buttonPrimary : props.theme.textPrimary)};
    background-color: ${props => props.theme.hoverBackground};
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    border-bottom: none;
    border-left: 3px solid ${(props) => (props.$active ? props.theme.buttonPrimary : "transparent")};
    text-align: left;
    width: 100%;
  }
`

const EmptyFeed = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  box-shadow: ${props => props.theme.shadow};
`

const EmptyTitle = styled.h2`
  margin-bottom: 8px;
  color: ${props => props.theme.textPrimary};
`

const EmptyText = styled.p`
  color: ${props => props.theme.textSecondary};
  margin-bottom: 20px;
`

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.error};
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.error};
  border-radius: 8px;
`

// 대시보드 스타일
const Dashboard = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

const StatCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  padding: 24px;
  box-shadow: ${props => props.theme.shadow};
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid ${props => props.theme.border};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadowHover};
    border-color: ${props => props.theme.buttonPrimary};
  }
  
  @media (max-width: 768px) {
    padding: 20px;
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    gap: 10px;
  }
`

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${props => props.$color || '#f3f4f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`

const StatContent = styled.div`
  flex: 1;
`

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
`

const StatLabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  margin-top: 2px;
`

// 필터 및 검색 스타일
const FilterSection = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  padding: 16px;
  box-shadow: ${props => props.theme.shadow};
  margin-bottom: 20px;
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const SearchBar = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  background-color: ${props => props.theme.inputBackground};
  border-radius: 6px;
  padding: 8px 12px;
  min-width: 200px;
  
  input {
    border: none;
    background: none;
    flex: 1;
    margin-left: 8px;
    outline: none;
    font-size: 14px;
    color: ${props => props.theme.textPrimary};
    
    &::placeholder {
      color: ${props => props.theme.textMuted};
    }
  }
`

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background-color: white;
  color: #374151;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f9fafb;
    border-color: #d1d5db;
  }
`

const ViewToggle = styled.div`
  display: flex;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  overflow: hidden;
`

const ViewButton = styled.button`
  padding: 8px 12px;
  border: none;
  background-color: ${props => props.$active ? props.theme.buttonPrimary : props.theme.cardBackground};
  color: ${props => props.$active ? 'white' : props.theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.$active ? props.theme.buttonPrimaryHover : props.theme.hoverBackground};
  }
`

// 게시물 그리드 스타일
const PostGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`

const PostList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

// 추가 버튼 스타일
const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-left: auto;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    border-color: ${props => props.theme.border};
  }
`

export default function MyFeed() {
  const { isLoggedIn, user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("my");
  const [posts, setPosts] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalLikes: 0,
    totalComments: 0,
    totalFollowers: 0
  });

  // 사용자 통계 가져오기
  const fetchUserStats = async () => {
    try {
      const response = await fetch(`/user/stats/${user.id}`, {
        credentials: 'include'
      });
      
      const data = await response.json();
      if (data.success) {
        setStats({
          totalPosts: data.stats.totalPosts || 0,
          totalLikes: data.stats.totalLikes || 0,
          totalComments: data.stats.totalComments || 0,
          totalFollowers: data.stats.followersCount || 0
        });
      }
    } catch (error) {
      console.error("사용자 통계 로드 오류:", error);
    }
  };

  // 내 게시물 가져오기
  const fetchMyPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/post/user/${user.id}?limit=50`, {
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log("내 게시물 로드 성공:", data.posts);
        setPosts(data.posts);
      } else {
        console.error("내 게시물 로드 실패:", data.message);
        setError(data.message || "게시물을 불러오는데 실패했습니다.");
      }
    } catch (error) {
      console.error("내 게시물 로드 오류:", error);
      setError("서버 연결에 문제가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 저장한 게시물 가져오기
  const fetchSavedPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/bookmark/list?limit=50', {
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log("저장한 게시물 로드 성공:", data.bookmarks);
        setSavedPosts(data.bookmarks);
      } else {
        console.error("저장한 게시물 로드 실패:", data.message);
        setError(data.message || "저장한 게시물을 불러오는데 실패했습니다.");
      }
    } catch (error) {
      console.error("저장한 게시물 로드 오류:", error);
      setError("서버 연결에 문제가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 좋아요한 게시물 가져오기
  const fetchLikedPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/post/liked/${user.id}?limit=50`, {
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log("좋아요한 게시물 로드 성공:", data.posts);
        setLikedPosts(data.posts);
      } else {
        console.error("좋아요한 게시물 로드 실패:", data.message);
        setError(data.message || "좋아요한 게시물을 불러오는데 실패했습니다.");
      }
    } catch (error) {
      console.error("좋아요한 게시물 로드 오류:", error);
      setError("서버 연결에 문제가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 탭 변경 시 해당 데이터 로드
  useEffect(() => {
    if (!isLoggedIn || !user) return;
    
    if (activeTab === "my") {
      fetchMyPosts();
      fetchUserStats();
    } else if (activeTab === "saved") {
      fetchSavedPosts();
    } else if (activeTab === "liked") {
      fetchLikedPosts();
    }
  }, [activeTab, isLoggedIn, user]);

  // 필터링된 게시물 가져오기
  const getFilteredPosts = () => {
    let currentPosts = getDisplayPosts();
    
    if (searchQuery) {
      currentPosts = currentPosts.filter(post =>
        post.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (post.hashtags && post.hashtags.some(tag => 
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        ))
      );
    }
    
    return currentPosts;
  };

  // 현재 탭에 따라 표시할 게시물 선택
  const getDisplayPosts = () => {
    switch (activeTab) {
      case "my":
        return posts;
      case "saved":
        return savedPosts;
      case "liked":
        return likedPosts;
      default:
        return [];
    }
  };

  const handleCreatePost = () => {
    // PostCreator가 있는 HomePage로 이동
    navigate("/");
  };

  const handleExportData = () => {
    const dataToExport = {
      stats,
      posts: getDisplayPosts(),
      exportDate: new Date().toISOString(),
      user: user.username
    };
    
    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my_feed_data_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const currentPosts = getFilteredPosts();

  return (
    <Container>
      <Header>
        <Title theme={theme}>내 피드</Title>
        <ActionButtons>
          <ActionButton theme={theme} onClick={() => navigate('/settings')}>
            <Settings size={16} />
            설정
          </ActionButton>
          <ActionButton theme={theme} onClick={handleExportData}>
            <Share size={16} />
            데이터 내보내기
          </ActionButton>
          <CreatePostButton theme={theme} onClick={handleCreatePost}>
            <PlusCircle size={16} />
            <span>새 게시물</span>
          </CreatePostButton>
        </ActionButtons>
      </Header>

      {/* 대시보드 - 내 게시물 탭에만 표시 */}
      {activeTab === "my" && (
        <Dashboard>
          <StatCard theme={theme}>
            <StatIcon $color="#2563eb">
              <BarChart3 size={24} />
            </StatIcon>
            <StatContent>
              <StatNumber theme={theme}>{stats.totalPosts}</StatNumber>
              <StatLabel theme={theme}>총 게시물</StatLabel>
            </StatContent>
          </StatCard>
          
          <StatCard theme={theme}>
            <StatIcon $color="#dc2626">
              <Heart size={24} />
            </StatIcon>
            <StatContent>
              <StatNumber theme={theme}>{stats.totalLikes}</StatNumber>
              <StatLabel theme={theme}>받은 좋아요</StatLabel>
            </StatContent>
          </StatCard>
          
          <StatCard theme={theme}>
            <StatIcon $color="#059669">
              <MessageSquare size={24} />
            </StatIcon>
            <StatContent>
              <StatNumber theme={theme}>{stats.totalComments}</StatNumber>
              <StatLabel theme={theme}>받은 댓글</StatLabel>
            </StatContent>
          </StatCard>
          
          <StatCard theme={theme} onClick={() => navigate(`/profile/${user.username}`)}>
            <StatIcon $color="#7c3aed">
              <Users size={24} />
            </StatIcon>
            <StatContent>
              <StatNumber theme={theme}>{stats.totalFollowers}</StatNumber>
              <StatLabel theme={theme}>팔로워</StatLabel>
            </StatContent>
          </StatCard>
        </Dashboard>
      )}

      {/* 검색 및 필터 섹션 */}
      <FilterSection theme={theme}>
        <SearchBar theme={theme}>
          <Search size={20} color={theme.textMuted} />
          <input
            type="text"
            placeholder="내 게시물 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBar>
        
        <ViewToggle theme={theme}>
          <ViewButton 
            theme={theme}
            $active={viewMode === 'list'} 
            onClick={() => setViewMode('list')}
          >
            <List size={16} />
          </ViewButton>
          <ViewButton 
            theme={theme}
            $active={viewMode === 'grid'} 
            onClick={() => setViewMode('grid')}
          >
            <Grid size={16} />
          </ViewButton>
        </ViewToggle>
      </FilterSection>

      <FeedTabs theme={theme}>
        <FeedTab theme={theme} $active={activeTab === "my"} onClick={() => setActiveTab("my")}>
          내 게시물
        </FeedTab>
        <FeedTab theme={theme} $active={activeTab === "saved"} onClick={() => setActiveTab("saved")}>
          저장된 게시물
        </FeedTab>
        <FeedTab theme={theme} $active={activeTab === "liked"} onClick={() => setActiveTab("liked")}>
          좋아요한 게시물
        </FeedTab>
      </FeedTabs>

      {loading ? (
        <LoadingIndicator theme={theme}>
          <Loader size={20} className="animate-spin" />
          <span>게시물을 불러오는 중...</span>
        </LoadingIndicator>
      ) : error ? (
        <ErrorMessage theme={theme}>{error}</ErrorMessage>
      ) : currentPosts.length > 0 ? (
        viewMode === 'grid' ? (
          <PostGrid>
            {currentPosts.map(post => (
              <PostCard
                key={post.id}
                post={{
                  id: post.id,
                  author: post.author_username,
                  authorAvatar: post.author_avatar,
                  timestamp: post.created_at,
                  images: post.images || [],
                  tags: post.tags || [],
                  description: post.content,
                  like_count: post.like_count,
                  comment_count: post.comment_count
                }}
                onPostUpdated={() => {
                  // 게시물 업데이트 후 다시 로드
                  if (activeTab === "my") {
                    fetchMyPosts();
                  }
                }}
                onPostDeleted={() => {
                  // 게시물 삭제 후 다시 로드
                  if (activeTab === "my") {
                    fetchMyPosts();
                  }
                }}
              />
            ))}
          </PostGrid>
        ) : (
          <PostList>
            {currentPosts.map(post => (
              <PostCard
                key={post.id}
                post={{
                  id: post.id,
                  author: post.author_username,
                  authorAvatar: post.author_avatar,
                  timestamp: post.created_at,
                  images: post.images || [],
                  tags: post.tags || [],
                  description: post.content,
                  like_count: post.like_count,
                  comment_count: post.comment_count
                }}
                onPostUpdated={() => {
                  if (activeTab === "my") {
                    fetchMyPosts();
                  }
                }}
                onPostDeleted={() => {
                  if (activeTab === "my") {
                    fetchMyPosts();
                  }
                }}
              />
            ))}
          </PostList>
        )
      ) : (
        <EmptyFeed theme={theme}>
          <EmptyTitle theme={theme}>게시물이 없습니다</EmptyTitle>
          <EmptyText theme={theme}>
            {activeTab === "my"
              ? "아직 게시물을 작성하지 않았습니다. 새 게시물을 작성해보세요!"
              : activeTab === "saved"
                ? "저장한 게시물이 없습니다. 관심 있는 게시물을 저장해보세요!"
                : "좋아요한 게시물이 없습니다. 마음에 드는 게시물에 좋아요를 눌러보세요!"}
          </EmptyText>
          {activeTab === "my" && (
            <CreatePostButton theme={theme} onClick={handleCreatePost}>
              <PlusCircle size={16} />
              <span>새 게시물 작성하기</span>
            </CreatePostButton>
          )}
        </EmptyFeed>
      )}
    </Container>
  );
}